import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { API_BASE_URL, ASSUME_ROLE_ENABLED } from "../../helpers/config";
import { IPromoCodeDto } from "@/store/promoCodes/types";
import { IStandardOfferDto } from "@/store/standardOffer/types";
import {
  IReferenceDataItem,
  IReferenceData,
} from "@/store/referenceData/types";
import { AUTH } from "@/store/auth";
import {
  IApplicationDto,
  IApplicationStatus,
  IIfcsDetails,
} from "../../store/applications/types";
import { AUTH_GETTERS } from "../../store/auth/getters";
import store from "@/store";
import qs from "qs";
import {
  ApplicationModel,
  ApplicationModelSchema,
} from "./types/ApplicationModel";
import { z } from "zod";

const DEFAULT_PAGINATION_LIMIT = 10;

// create axios client
const http: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// get an access token before each request
http.interceptors.request.use((config: AxiosRequestConfig): any => {
  if (ASSUME_ROLE_ENABLED) {
    const assumedRole: string | undefined =
      store.getters[AUTH.NS(AUTH_GETTERS.GET_ASSUMED_ROLE)];

    if (assumedRole) {
      config.headers["Assume-Role"] = assumedRole;
    }
  }

  return store.dispatch(AUTH.NS(AUTH.ACTIONS.ACQUIRE_TOKEN)).then(
    (token) => {
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    },
    (err) => {
      console.log(err);
      return err;
    }
  );
});

export default {
  getBanksData: (): Promise<any> => http.get("/v1/banks"),
  getReferenceData: (): Promise<any> => http.get("/v1/referenceData"),
  updateReferenceData: (id: number, item: IReferenceData): Promise<any> => {
    return http.put(`/v1/referenceData/${id}`, item);
  },
  createReferenceDataItem: (
    referenceDataId: number,
    item: IReferenceDataItem
  ): Promise<any> => {
    return http.post(`/v1/referenceData/${referenceDataId}/items`, {
      details: item,
    });
  },
  createStandardOffer: (offer: IStandardOfferDto): Promise<any> => {
    return http.post("/v1/standardOffers", { standardOffer: offer });
  },
  updateStandardOffer: (offer: IStandardOfferDto, id: number): Promise<any> => {
    return http.put(`/v1/standardOffers/${id}`, { standardOffer: offer });
  },
  getStandardOffers: (): Promise<any> => {
    return http.get("/v1/standardOffers", {
      params: {
        limit: 250,
      },
    });
  },
  getStandardOffer: (id: number): Promise<any> => {
    return http.get(`/v1/standardOffers/${id}`);
  },
  getStandardOfferByOfferId: (offerId: number): Promise<any> => {
    return http.get(`/v1/offers/${offerId}/standardOffers`);
  },
  createPromoOffer: (promoCode: IPromoCodeDto): Promise<any> => {
    return http.post("/v1/promoOffers", { promoOffer: promoCode });
  },
  updatePromoOffer: (promoCode: IPromoCodeDto, id: number): Promise<any> => {
    return http.put(`/v1/promoOffers/${id}`, { promoOffer: promoCode });
  },
  getPromoOffers: (): Promise<any> => {
    return http.get("/v1/promoOffers", { params: { limit: 250 } });
  },
  getPromoOffer: (id: number): Promise<any> => {
    return http.get(`/v1/promoOffers/${id}`);
  },
  getPromoOfferByOfferId: (offerId: number): Promise<any> => {
    return http.get(`/v1/offers/${offerId}/promoOffers`);
  },
  getApplications: (
    statuses: IApplicationStatus[],
    pagination: any
  ): Promise<any> => {
    return http.get("/v1/applications", {
      params: {
        statuses,
        filter: pagination.search,
        offset: pagination.offset,
        limit: pagination.rowsPerPage || DEFAULT_PAGINATION_LIMIT,
        sortBy: pagination.sortBy,
        sortDirection: pagination.sortDirection,
      },
      paramsSerializer: (params) => {
        return qs.stringify(params);
      },
    });
  },
  getMyApplications: (
    statuses: IApplicationStatus[],
    pagination: any
  ): Promise<any> => {
    return http.get("/v1/applications/mine", {
      params: {
        statuses,
        filter: pagination.search,
        offset: pagination.offset,
        limit: pagination.rowsPerPage || DEFAULT_PAGINATION_LIMIT,
        sortBy: pagination.sortBy,
        sortDirection: pagination.sortDirection,
      },
      paramsSerializer: (params) => {
        return qs.stringify(params);
      },
    });
  },
  getApplication: async (id: string): Promise<ApplicationModel> => {
    const response = await http.get<ApplicationModel>(`/v1/applications/${id}`);
    return ApplicationModelSchema.parse(response.data);
  },
  getDrafts: (pagination: any): Promise<any> => {
    return http.get("/v1/drafts", {
      params: {
        filter: pagination.search,
        offset: pagination.offset,
        limit: pagination.rowsPerPage || DEFAULT_PAGINATION_LIMIT,
        sortBy: pagination.sortBy,
        sortDirection: pagination.sortDirection,
      },
    });
  },
  getApplicationTimeline: (id: string): Promise<any> => {
    return http.get(`/v1/applications/${id}/timeline`);
  },
  updateApplication: (id: string, data: IApplicationDto): Promise<any> => {
    return http.put(`/v1/applications/${id}/update`, {
      details: data,
    });
  },
  updateIfcsDetails: (id: string, data: IIfcsDetails): Promise<any> => {
    return http.put(`/v1/applications/${id}/updateIfcsDetails`, {
      details: data,
    });
  },
  cancelApplication: (id: string, cancellationReason: string): Promise<any> => {
    return http.put(`/v1/applications/${id}/cancel`, {
      cancellationReason,
    });
  },
  validateFlyBuys: (flyBuysNumber: any) =>
    http.post("/v1/flybuys/validate", {
      flyBuysNumber,
    }),
  validateAirpoints: (
    membershipNumber: any,
    firstName: string,
    familyName: string
  ) =>
    http.post("/v1/airpoints/membershipnumber/validate", {
      membershipNumber,
      firstName,
      familyName,
    }),
  searchAddresses: (query: any) =>
    http.get("/v1/addresses", {
      params: {
        query,
      },
    }),
  addressDetails: (dpid: any) => http.get(`/v1/addresses/${dpid}`),
  changeCreditLimit: (id: number, creditLimit: number): Promise<any> => {
    return http.post(`/v1/applications/${id}/changeCreditLimit`, {
      creditLimit,
    });
  },
  // Tasks
  assignTask: (taskId: number): Promise<any> => {
    return http.put(`/v1/tasks/${taskId}/assignToMe`);
  },
  unassignTask: (taskId: number): Promise<any> => {
    return http.put(`/v1/tasks/${taskId}/unassign`);
  },
  getApplicationTasks: (id: number): Promise<any> => {
    return http.get(`/v1/applications/${id}/tasks`);
  },
  completeCallBackOutcome: (taskId: number, comment: string): Promise<any> => {
    return http.put(`/v1/tasks/${taskId}/callbackOutcome`, {
      outcome: "Completed",
      comment,
    });
  },
  declineCallBackOutcome: (taskId: number, comment: string): Promise<any> => {
    return http.put(`/v1/tasks/${taskId}/callbackOutcome`, {
      outcome: "Declined",
      comment,
    });
  },
  creditTeamDeclined: (
    taskId: number,
    creditDecisionChangeReason: string
  ): Promise<any> => {
    return http.put(`/v1/tasks/${taskId}/creditTeamOutcome`, {
      outcome: "Declined",
      creditDecisionChangeReason,
    });
  },
  creditTeamApproved: (
    taskId: number,
    creditDecisionChangeReason: string,
    creditLimit: number
  ): Promise<any> => {
    return http.put(`/v1/tasks/${taskId}/creditTeamOutcome`, {
      outcome: "Approved",
      creditDecisionChangeReason,
      creditLimit,
    });
  },
  creditTeamNeedPg: (
    taskId: number,
    creditDecisionChangeReason: string,
    creditLimit: number
  ): Promise<any> => {
    return http.put(`/v1/tasks/${taskId}/creditTeamOutcome`, {
      outcome: "NeedPersonalGuarantee",
      creditDecisionChangeReason,
      creditLimit,
    });
  },
  personalGuaranteeObtained: (
    taskId: number,
    obtainedByEmailOrPost: boolean,
    fileLocation: string
  ): Promise<any> => {
    return http.put(`/v1/tasks/${taskId}/obtainPersonalGuaranteeOutcome`, {
      obtainedByEmailOrPost,
      fileLocation,
    });
  },
  cardAccountSetup: (taskId: number, accountNumber: string): Promise<any> => {
    return http.put(`/v1/tasks/${taskId}/cardAccountSetup`, {
      accountNumber,
    });
  },
  modifyAccountNumber: (
    taskId: number,
    accountNumber: string
  ): Promise<any> => {
    return http.put(`/v1/tasks/${taskId}/modifyAccountNumber`, {
      accountNumber,
    });
  },
  jdeAccountSetup: (taskId: number, accountNumber: string): Promise<any> => {
    return http.put(`/v1/tasks/${taskId}/jdeAccountSetup`, {
      accountNumber,
    });
  },
};
